/**
 * Gemeinsame Angaben der Sammelaufnahme.
 * Beim Weitergehen gelten sie für jedes Feld, das das Tier nicht bewusst
 * selbst gesetzt hat. Die Markierung steht in ownFields, nicht im Vergleich
 * mit dem gemeinsamen Wert: ein eigener Wert bleibt auch dann, wenn er
 * zufällig gleich ist.
 */
export const SHARED_INTAKE_FIELD_KEYS = [
  "careSiteId",
  "placeId",
  "foundOn",
  "foundPlaceId",
  "foundPlaceText",
  "foundPlaceExtra",
  "selectedReasons",
  "reasonNote",
] as const;

export type SharedIntakeFieldKey = (typeof SHARED_INTAKE_FIELD_KEYS)[number];

export type SharedIntakeFields = {
  careSiteId: string | null;
  placeId: string | null;
  foundOn: string | null;
  foundPlaceId: string | null;
  foundPlaceText: string;
  foundPlaceExtra: string;
  selectedReasons: ReadonlySet<string>;
  reasonNote: string;
};

type IntakeAnimal = SharedIntakeFields & {
  sharedOrigin?: SharedIntakeFields;
  ownFields?: ReadonlySet<SharedIntakeFieldKey>;
};

function snapshotShared(shared: SharedIntakeFields): SharedIntakeFields {
  return {
    careSiteId: shared.careSiteId,
    placeId: shared.placeId,
    foundOn: shared.foundOn,
    foundPlaceId: shared.foundPlaceId,
    foundPlaceText: shared.foundPlaceText,
    foundPlaceExtra: shared.foundPlaceExtra,
    selectedReasons: new Set(shared.selectedReasons),
    reasonNote: shared.reasonNote,
  };
}

/** Felder aus einem Patch als bewusst gesetzt merken. Andere Schlüssel bleiben außen vor. */
export function markOwnIntakeFields(
  own: ReadonlySet<SharedIntakeFieldKey> | undefined,
  patch: object,
): Set<SharedIntakeFieldKey> {
  const next = new Set(own);
  for (const key of SHARED_INTAKE_FIELD_KEYS) {
    if (Object.prototype.hasOwnProperty.call(patch, key)) next.add(key);
  }
  return next;
}

export function applySharedIntake<T extends IntakeAnimal>(
  animals: T[],
  shared: SharedIntakeFields,
): (T & { sharedOrigin: SharedIntakeFields; ownFields: Set<SharedIntakeFieldKey> })[] {
  return animals.map((animal) => {
    const ownFields = new Set(animal.ownFields);
    const reasons = ownFields.has("selectedReasons")
      ? animal.selectedReasons
      : new Set(shared.selectedReasons);
    return {
      ...animal,
      careSiteId: ownFields.has("careSiteId") ? animal.careSiteId : shared.careSiteId,
      placeId: ownFields.has("placeId") ? animal.placeId : shared.placeId,
      foundOn: ownFields.has("foundOn") ? animal.foundOn : shared.foundOn,
      foundPlaceId: ownFields.has("foundPlaceId") ? animal.foundPlaceId : shared.foundPlaceId,
      foundPlaceText: ownFields.has("foundPlaceText") ? animal.foundPlaceText : shared.foundPlaceText,
      foundPlaceExtra: ownFields.has("foundPlaceExtra")
        ? animal.foundPlaceExtra
        : shared.foundPlaceExtra,
      reasonNote: ownFields.has("reasonNote") ? animal.reasonNote : shared.reasonNote,
      selectedReasons: reasons as T["selectedReasons"],
      sharedOrigin: snapshotShared(shared),
      ownFields,
    };
  });
}
